import { defineStore } from 'pinia'
import { ref } from 'vue'
import { User } from 'firebase/auth'
import { auth } from '@/config/firebase'
import { FirebaseError } from 'firebase/app'
import { getAuthErrorMessage } from '@/utils/auth-errors'
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  sendPasswordResetEmail
} from 'firebase/auth'

interface RegisterData {
  email: string
  password: string
  name: string
}

interface LoginData {
  email: string
  password: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)
  const initialized = ref(false)

  const setUser = (newUser: User | null) => {
    user.value = newUser
  }

  const setLoading = (status: boolean) => {
    loading.value = status
  }

  const setError = (message: string | null) => {
    error.value = message
  }

  let unsubscribeAuth: (() => void) | null = null

  const init = async () => {
    if (initialized.value) return

    try {
      await setPersistence(auth, browserLocalPersistence)
    } catch (err) {
      console.error('Error setting persistence:', err)
    }

    if (unsubscribeAuth) {
      unsubscribeAuth()
      unsubscribeAuth = null
    }

    unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser)
      setLoading(false)
      initialized.value = true
    })
  }

  const register = async ({ email, password, name }: RegisterData) => {
    setLoading(true)
    setError(null)
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      await updateProfile(userCredential.user, { displayName: name })
      setUser(userCredential.user)
      return userCredential.user
    } catch (err: any) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const login = async ({ email, password }: LoginData) => {
    setLoading(true)
    setError(null)
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      setUser(userCredential.user)
      return userCredential.user
    } catch (err: any) {
      const errorMessage = err instanceof FirebaseError ? getAuthErrorMessage(err) : err.message
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      await firebaseSignOut(auth)
      setUser(null)
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const cleanup = () => {
    if (unsubscribeAuth) {
      unsubscribeAuth()
      unsubscribeAuth = null
    }
    initialized.value = false
    setUser(null)
    setLoading(true)
    setError(null)
  }

  const resetPassword = async (email: string) => {
    setLoading(true)
    setError(null)
    try {
      await sendPasswordResetEmail(auth, email)
    } catch (err: any) {
      const errorMessage = err instanceof FirebaseError ? getAuthErrorMessage(err) : err.message
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    user,
    loading,
    error,
    init,
    setUser,
    setLoading,
    setError,
    register,
    login,
    signOut,
    cleanup,
    resetPassword
  }
})